/**
 * ============================================================================
 * SECURITY PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for managing security settings and features
 */

import React, { useState } from 'react';
import { UserDashboardLayout } from '../components/user';
import SecuritySettings from '../components/security/SecuritySettings';
import TwoFactorSetup from '../components/security/TwoFactorSetup';
import SecurityActivity from '../components/security/SecurityActivity';
import SecurityAlerts from '../components/security/SecurityAlerts';
import SessionManager from '../components/security/SessionManager';
import PasswordStrength from '../components/security/PasswordStrength';
import Card from '../components/common/Card';
import Tabs from '../components/common/Tabs';
import { useI18n } from '../providers';
import { useTwoFactor } from '../hooks/useTwoFactor';
import { useSecurity } from '../hooks/useSecurity';

/**
 * Security Page Component
 * @returns {JSX.Element}
 */
const Security = () => {
  const { language } = useI18n();
  const { isEnabled: twoFactorEnabled } = useTwoFactor();
  const { securitySettings } = useSecurity();
  const [activeTab, setActiveTab] = useState('settings');

  const tabs = [
    { id: 'settings', label: language === 'fr' ? 'Paramètres' : 'Settings', icon: '⚙️' },
    { id: 'twoFactor', label: language === 'fr' ? 'Authentification à deux facteurs' : 'Two-Factor Auth', icon: '🔐' },
    { id: 'sessions', label: language === 'fr' ? 'Sessions' : 'Sessions', icon: '🔑' },
    { id: 'activity', label: language === 'fr' ? 'Activité' : 'Activity', icon: '📋' },
    { id: 'alerts', label: language === 'fr' ? 'Alertes' : 'Alerts', icon: '🔔' },
  ];

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
          🛡️ {language === 'fr' ? 'Sécurité' : 'Security'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Gérez vos paramètres de sécurité et protégez votre compte'
            : 'Manage your security settings and protect your account'}
        </p>
      </div>

      {/* Security Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <Card variant="glass" className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-2xl shadow-lg">
              🔐
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                {language === 'fr' ? '2FA' : '2FA'}
              </p>
              <p className="text-lg font-black text-gray-900 dark:text-white">
                {twoFactorEnabled
                  ? language === 'fr' ? 'Activé' : 'Enabled'
                  : language === 'fr' ? 'Désactivé' : 'Disabled'}
              </p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-2xl shadow-lg">
              🛡️
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                {language === 'fr' ? 'Sécurité' : 'Security'}
              </p>
              <p className="text-lg font-black text-gray-900 dark:text-white">
                {securitySettings?.suspiciousActivityAlerts
                  ? language === 'fr' ? 'Protégé' : 'Protected'
                  : language === 'fr' ? 'Basique' : 'Basic'}
              </p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl shadow-lg">
              📋
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                {language === 'fr' ? 'Alertes' : 'Alerts'}
              </p>
              <p className="text-lg font-black text-gray-900 dark:text-white">
                {language === 'fr' ? 'Actives' : 'Active'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          variant="pills"
        />
      </Card>

      {/* Tab Content */}
      {activeTab === 'settings' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
          <SecuritySettings />
          <PasswordStrength />
        </div>
      )}

      {activeTab === 'twoFactor' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
          <TwoFactorSetup />
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
          <SessionManager />
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
          <SecurityActivity />
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
          <SecurityAlerts />
        </div>
      )}
    </UserDashboardLayout>
  );
};

export default Security;
